import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  Sparkles, 
  Check, 
  CheckCircle2,
  AlertCircle, 
  MessageSquareText, 
  UserRound, 
  MapPin, 
  SlidersHorizontal,
  ArrowRight,
  ArrowLeft,
  RotateCcw,
  Loader2,
  ChevronRight,
  ChevronDown,
  Scale,
  Wallet,
  Eye
} from "lucide-react";
import Button from "../components/Button";
import MatchBreakdown from "../components/MatchBreakdown";
import FilterChip from "../components/FilterChip";
import { Blob, Star } from "../components/Decor";
import { schools, CITIES, LEVELS, CURRICULA } from "../data/schools";
import { runMatch } from "../lib/matching";
import { useCompare } from "../context/CompareContext";
import SEO from "../components/SEO";

const INTERESTS = ["Coding", "Olahraga", "Seni", "Sains", "Tahfidz", "Bahasa"];
const RELIGIOUS_PROGRAMS = ["Islam Terpadu"];
const BUDGET_OPTIONS = [
  { label: "Tanpa batas", value: null },
  { label: "≤ Rp2 jt", value: 2000000 },
  { label: "≤ Rp3 jt", value: 3000000 },
  { label: "≤ Rp5 jt", value: 5000000 },
];
const EXAMPLES = [
  "SD Islam di Bandung",
  "Budget maksimal Rp3 juta",
  "Anak suka coding & olahraga"
];

const STEPS = [
  { id: 1, label: "Ceritakan", icon: MessageSquareText },
  { id: 2, label: "Profil Anak", icon: UserRound },
  { id: 3, label: "Lokasi & Budget", icon: MapPin },
  { id: 4, label: "Preferensi", icon: SlidersHorizontal },
  { id: 5, label: "Hasil", icon: Sparkles },
];

export default function Match() {
  const [step, setStep] = useState(1);
  const [text, setText] = useState("");
  const [level, setLevel] = useState(null);
  const [interests, setInterests] = useState([]);
  const [city, setCity] = useState(null);
  const [budget, setBudget] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [religiousProgram, setReligiousProgram] = useState(null);
  const [specialNeeds, setSpecialNeeds] = useState(undefined);
  
  const [result, setResult] = useState(null);
  const [emptyError, setEmptyError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toggleCompare, isInCompare } = useCompare();

  const handleStartOver = () => {
    setStep(1); 
    setText(""); 
    setLevel(null); 
    setInterests([]); 
    setCity(null);
    setBudget(null); 
    setCurriculum(null); 
    setReligiousProgram(null);
    setSpecialNeeds(undefined); 
    setResult(null);
    setEmptyError(false);
  };

  const handleMatch = () => {
    if (!text && !level && interests.length === 0 && !city && !budget && !curriculum && !religiousProgram && specialNeeds === undefined) {
      setEmptyError(true);
      return;
    }
    
    setEmptyError(false);
    setIsLoading(true);
    
    // Explicit selection overrides
    const finalCurriculum = religiousProgram === "Islam Terpadu" ? "Islam Terpadu" : curriculum;
    const controls = {
      city, level, budget, curriculum: finalCurriculum, interests, specialNeeds
    };
    
    // Fake loading delay to make it feel like AI is thinking
    setTimeout(() => {
      const res = runMatch(schools, text, controls, 3);
      setResult(res);
      setStep(5);
      setIsLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };

  const nextStep = () => {
    setStep(s => Math.min(s + 1, 4));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const prevStep = () => {
    setStep(s => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleInterest = (val) => {
    setInterests(prev => 
      prev.includes(val) ? prev.filter(i => i !== val) : [...prev, val]
    );
  };

  return (
    <div className="relative overflow-hidden min-h-[calc(100vh-80px)]">
      <SEO
        title="AI School Match | Temukan Sekolah yang Cocok | MySchoolMate"
        description="Ceritakan kebutuhan anak dan keluarga untuk mendapatkan rekomendasi sekolah yang lebih sesuai melalui AI School Match."
        canonicalPath="/match"
      />
      <Blob className="absolute -top-24 -right-24 w-96 h-96 opacity-30" color="#73DCC6" />
      <Blob className="absolute top-1/2 -left-24 w-64 h-64 opacity-20" color="#F2A562" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
        
        {step < 5 && (
          <div className="text-center max-w-2xl mx-auto mb-10 animate-fade-in">
            <span className="inline-flex items-center gap-2 rounded-pill bg-yellow ink-border px-4 py-1.5 text-sm font-bold">
              <Star className="w-4 h-4" color="#211A4D" /> AI School Match
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold leading-tight">
              Temukan sekolah terbaik untuk masa depan anak Anda
            </h1>
            <p className="mt-4 text-ink/70 text-lg">
              Ceritakan kebutuhan keluarga dan anak dengan bahasa Anda sendiri.<br className="hidden sm:block" />
              AI kami akan membantu mencocokkannya dengan sekolah yang tepat.
            </p>
          </div>
        )}

        <MatchProgress currentStep={step} />

        <div className="mt-8 bg-white ink-border rounded-card shadow-offset-sm p-6 sm:p-10 relative">
          
          {step === 1 && (
            <div className="animate-fade-in grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
              <div className="flex flex-col h-full lg:pt-6">
                <h2 className="text-3xl font-extrabold leading-tight mb-2">
                  Ceritakan sekolah yang Anda cari
                </h2>
                <p className="text-ink/70 font-medium mb-6">
                  Tak perlu mengisi semuanya dengan sempurna.<br />
                  Ceritakan kebutuhan keluarga dan anak dengan bahasa Anda sendiri.
                </p>
                
                <label htmlFor="match-input" className="sr-only">Ceritakan kebutuhan sekolah</label>
                <div className="relative">
                  <textarea
                    id="match-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={6}
                    placeholder="Saya mencari SD Islam di Bandung, budget maksimal Rp3 juta per bulan. Anak saya suka coding dan olahraga. Saya juga ingin sekolah dengan kelas yang tidak terlalu besar."
                    className="w-full bg-white ink-border rounded-2xl p-5 text-lg resize-none placeholder:text-ink/40 focus:outline-none focus:ring-4 focus:ring-purple/20 transition-all pb-10"
                  />
                  <span className="absolute bottom-4 right-5 text-xs text-ink/40 font-medium font-mono">
                    {text.length} / 1000
                  </span>
                </div>
                
                <p className="mt-6 text-sm font-semibold text-ink/60 mb-3">
                  Coba mulai dengan ide berikut:
                </p>
                <div className="flex flex-wrap gap-2">
                  {EXAMPLES.map(ex => (
                    <button
                      key={ex}
                      onClick={() => setText(prev => prev ? prev + " " + ex : ex)}
                      className="text-sm font-semibold text-purple bg-purple/5 border border-purple/20 hover:bg-purple/10 px-4 py-2 rounded-full transition-colors"
                    >
                      + {ex}
                    </button>
                  ))}
                </div>
                
                
                <div className="mt-auto pt-8 flex w-full lg:mb-4">
                  <Button onClick={nextStep} className="w-full justify-center">
                    Lanjut <ArrowRight size={18} className="ml-1" />
                  </Button>
                </div>
              </div>
              
              <div className="hidden lg:block relative -mx-4 -my-4 lg:mx-0 lg:my-0">
                <img 
                  src="/assets/landing/image-aimatch.png" 
                  alt="Ilustrasi cara kerja AI School Match" 
                  className="w-full h-auto object-contain rounded-2xl" 
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fade-in">
              <h2 className="text-3xl font-extrabold leading-tight mb-2">
                Kenali kebutuhan anak
              </h2>
              <p className="text-ink/70 font-medium mb-8">
                Setiap anak berbeda. Pilih beberapa hal yang membantu kami memahami pilihan sekolah yang lebih cocok.
              </p>
              
              <div className="space-y-8">
                <ControlGroup label="Jenjang yang dicari">
                  <FilterChip active={!level} onClick={() => setLevel(null)}>Bebas</FilterChip>
                  {LEVELS.map((l) => (
                    <FilterChip key={l} active={level === l} onClick={() => setLevel(l)}>
                      {l}
                    </FilterChip>
                  ))}
                </ControlGroup>
                
                <ControlGroup label="Minat anak">
                  {INTERESTS.map((i) => (
                    <FilterChip key={i} active={interests.includes(i)} onClick={() => toggleInterest(i)}>
                      {i}
                    </FilterChip>
                  ))}
                </ControlGroup>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="animate-fade-in">
              <h2 className="text-3xl font-extrabold leading-tight mb-2">
                Sesuaikan dengan kebutuhan keluarga
              </h2>
              <p className="text-ink/70 font-medium mb-8">
                Tentukan batasan praktis agar kami merekomendasikan pilihan yang realistis.
              </p>
              
              <div className="space-y-8">
                <ControlGroup label="Kota">
                  <FilterChip active={!city} onClick={() => setCity(null)}>Bebas</FilterChip>
                  {CITIES.map((c) => (
                    <FilterChip key={c} active={city === c} onClick={() => setCity(c)}>
                      {c}
                    </FilterChip>
                  ))}
                </ControlGroup>
                
                <ControlGroup label="Budget maksimal / bulan">
                  {BUDGET_OPTIONS.map((b) => (
                    <FilterChip
                      key={b.label}
                      active={budget === b.value}
                      onClick={() => setBudget(b.value)}
                    >
                      {b.label}
                    </FilterChip>
                  ))}
                </ControlGroup>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="animate-fade-in">
              <h2 className="text-3xl font-extrabold leading-tight mb-2">
                Apa yang penting dari sekolah?
              </h2>
              <p className="text-ink/70 font-medium mb-8">
                Pilih prioritas tambahan yang Anda harapkan ada di sekolah tujuan.
              </p>
              
              {emptyError && (
                <div className="mb-6 p-4 rounded-xl bg-coral/10 border border-coral text-coral font-semibold flex items-start gap-3">
                  <AlertCircle className="shrink-0 mt-0.5" size={20} />
                  Pilih setidaknya satu kebutuhan agar kami dapat menemukan sekolah yang lebih sesuai.
                </div>
              )}
              
              <div className="space-y-8">
                <ControlGroup label="Kurikulum">
                  <FilterChip active={!curriculum} onClick={() => setCurriculum(null)}>Bebas</FilterChip>
                  {CURRICULA.filter(c => c !== "Islam Terpadu").map((c) => (
                    <FilterChip key={c} active={curriculum === c} onClick={() => setCurriculum(c)}>
                      {c}
                    </FilterChip>
                  ))}
                </ControlGroup>
                
                <ControlGroup label="Program Keagamaan">
                  <FilterChip active={!religiousProgram} onClick={() => setReligiousProgram(null)}>Tidak ada preferensi</FilterChip>
                  {RELIGIOUS_PROGRAMS.map((rp) => (
                    <FilterChip key={rp} active={religiousProgram === rp} onClick={() => setReligiousProgram(rp)}>
                      {rp}
                    </FilterChip>
                  ))}
                </ControlGroup>
                
                <ControlGroup label="Kebutuhan Khusus">
                  <FilterChip active={specialNeeds === undefined} onClick={() => setSpecialNeeds(undefined)}>Bebas</FilterChip>
                  <FilterChip active={specialNeeds === true} onClick={() => setSpecialNeeds(true)}>
                    Membutuhkan dukungan kebutuhan khusus
                  </FilterChip>
                </ControlGroup>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="animate-fade-in">
              <div className="text-center mb-10">
                <span className="inline-flex items-center gap-2 rounded-pill bg-yellow ink-border px-4 py-1.5 text-sm font-bold mb-4">
                  <Star className="w-4 h-4" color="#211A4D" /> AI School Match
                </span>
                <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-3">
                  {result?.results?.length > 0 
                    ? "Ini sekolah yang paling sesuai dengan kebutuhan Anda."
                    : "Belum menemukan pilihan yang sesuai."}
                </h1>
                <p className="text-ink/70 font-medium">
                  {result?.results?.length > 0 
                    ? "Rekomendasi dibuat berdasarkan preferensi yang Anda ceritakan dan pilih."
                    : "Coba ubah atau kurangi preferensi untuk melihat lebih banyak pilihan."}
                </p>
              </div>

              {result?.results?.length > 0 ? (
                <div className="space-y-6">
                  {result.results.map((r, idx) => (
                    <ResultCard
                      key={r.school.id}
                      rank={idx + 1}
                      data={r}
                      onCompare={() => toggleCompare(r.school.slug)}
                      inCompare={isInCompare(r.school.slug)}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-12 flex justify-center">
                  <Button variant="yellow" onClick={() => setStep(4)}>Ubah Preferensi</Button>
                </div>
              )}
            </div>
          )}

          {/* Navigation Actions */}
          {step > 1 && step < 5 && (
            <div className="mt-10 pt-6 border-t border-ink/10 flex items-center justify-between">
              <button 
                onClick={prevStep}
                className="font-bold text-ink/70 hover:text-ink flex items-center gap-2 py-2 px-3 -ml-3 rounded-lg hover:bg-ink/5 transition-colors"
              >
                <ArrowLeft size={18} /> Kembali
              </button>
              
              {step < 4 ? (
                <Button onClick={nextStep}>
                  Lanjut <ArrowRight size={18} className="ml-1" />
                </Button>
              ) : (
                <Button variant="yellow" onClick={handleMatch} disabled={isLoading}>
                  {isLoading ? (
                    <><Loader2 size={18} className="mr-2 animate-spin" /> Menganalisis...</>
                  ) : (
                    <><Sparkles size={18} className="mr-1" /> Lihat Hasil Match</>
                  )}
                </Button>
              )}
            </div>
          )}

          {step === 5 && result?.results?.length > 0 && (
            <div className="mt-10 pt-6 border-t border-ink/10 flex flex-wrap items-center justify-between gap-4">
              <button 
                onClick={handleStartOver}
                className="font-bold text-ink/60 hover:text-ink flex items-center gap-2 py-2 px-4 rounded-lg hover:bg-ink/5 transition-colors"
              >
                <RotateCcw size={18} /> Mulai Ulang
              </button>
              
              <Button variant="secondary" onClick={() => setStep(4)}>
                Ubah Preferensi
              </Button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function MatchProgress({ currentStep }) {
  return (
    <div className="mb-6">
      {/* Mobile progress */}
      <div className="md:hidden flex flex-col items-center justify-center">
        <div className="text-sm font-bold text-ink/70 mb-2">
          Step {Math.min(currentStep, 5)} dari 5
        </div>
        <div className="flex gap-2">
          {STEPS.map(s => (
            <div 
              key={s.id} 
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                s.id === currentStep ? 'bg-purple' : s.id < currentStep ? 'bg-mint' : 'bg-ink/10'
              }`} 
            />
          ))}
        </div>
        <div className="font-extrabold mt-3 text-lg">
          {STEPS.find(s => s.id === currentStep)?.label}
        </div>
      </div>

      {/* Desktop progress */}
      <div className="hidden md:flex items-center justify-between relative max-w-3xl mx-auto w-full px-4">
        {STEPS.map((s, idx) => {
          const isCompleted = s.id < currentStep;
          const isCurrent = s.id === currentStep;
          const Icon = s.icon;
          
          return (
            <React.Fragment key={s.id}>
              {/* Step Node */}
              <div className="flex flex-col items-center relative z-10 w-24">
                <div 
                  className={`w-12 h-12 rounded-full border-2 flex items-center justify-center mb-2 transition-all duration-300 ${
                    isCompleted 
                      ? "bg-mint border-mint text-ink" 
                      : isCurrent 
                        ? "bg-purple border-purple text-warm shadow-[0_4px_15px_-3px_rgba(33,26,77,0.3)] scale-110" 
                        : "bg-warm/50 backdrop-blur-sm border-ink/10 text-ink/40"
                  }`}
                >
                  {isCompleted ? <Check size={22} className="animate-in zoom-in" /> : <Icon size={20} />}
                </div>
                <span 
                  className={`text-xs font-bold transition-all duration-300 ${
                    isCompleted ? "text-ink/80" : isCurrent ? "text-purple scale-105" : "text-ink/40"
                  }`}
                >
                  {s.label}
                </span>
              </div>

              {/* Connecting Line */}
              {idx < STEPS.length - 1 && (
                <div className="flex-1 h-1 mx-2 relative overflow-hidden rounded-full bg-ink/5 -translate-y-3">
                  <div 
                    className="absolute top-0 left-0 h-full bg-mint transition-all duration-500 ease-out" 
                    style={{ width: isCompleted ? '100%' : '0%' }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function ControlGroup({ label, children }) {
  return (
    <div>
      <h3 className="text-sm font-extrabold uppercase tracking-wider text-ink/50 mb-3">{label}</h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function ResultCard({ rank, data, onCompare, inCompare }) {
  const { school, score, breakdown, reasons, tradeoffs } = data;
  const [detailOpen, setDetailOpen] = useState(false);
  const contentRef = useRef(null);

  return (
    <div className="group bg-white ink-border rounded-2xl overflow-hidden shadow-sm transition-all duration-200 hover:-translate-y-1.5 hover:shadow-offset-sm">
      <div className="flex flex-col md:flex-row">
        {/* Image */}
        <div className="w-full md:w-56 h-48 md:h-auto shrink-0 overflow-hidden">
          <img
            onError={(e) => { e.target.style.display = "none"; }}
            src={school.image}
            alt={school.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>

        {/* Content */}
        <div className="flex-1 p-5 md:p-6">
          {/* Top: Info + Score Badge */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-extrabold text-ink/40 uppercase tracking-widest mb-1">
                #{rank} · {school.curriculum}
              </div>
              <h3 className="text-lg md:text-xl font-extrabold leading-tight text-ink mb-2">
                {school.name}
              </h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink/50">
                <span className="flex items-center gap-1"><MapPin size={14} /> {school.city} · {school.distanceKm} km</span>
                <span className="font-bold text-ink/70">Rp{school.monthlyTuition.toLocaleString("id-ID")} /bln</span>
              </div>
            </div>
            <div className="px-4 py-3 rounded-xl bg-purple/10 text-purple font-extrabold text-2xl leading-none shrink-0">
              {score}%
            </div>
          </div>

          {/* Middle: Reasons */}
          <div className="border-t border-ink/10 pt-4 mb-4">
            <p className="text-purple font-bold text-sm mb-3">Mengapa sekolah ini cocok</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {reasons.slice(0, 4).map((r) => (
                <p key={r} className="flex items-start gap-2 text-sm text-ink/70">
                  <CheckCircle2 size={16} className="text-mint shrink-0 mt-0.5" strokeWidth={2} />
                  <span>{r}</span>
                </p>
              ))}
            </div>
          </div>

          {/* Smooth Collapsible Details */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setDetailOpen((v) => !v)}
              className="text-xs font-bold text-ink/40 cursor-pointer hover:text-purple transition-colors select-none flex items-center gap-1.5"
            >
              <ChevronDown
                size={14}
                className={`transition-transform duration-300 ${detailOpen ? "rotate-180" : ""}`}
              />
              <span className="border-b border-dashed border-ink/30">
                {detailOpen ? "Sembunyikan Detail Skor" : "Lihat Detail Skor Kecocokan"}
              </span>
            </button>
            <div
              ref={contentRef}
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                maxHeight: detailOpen ? (contentRef.current?.scrollHeight || 500) + "px" : "0px",
                opacity: detailOpen ? 1 : 0,
              }}
            >
              <div className="pt-3 mt-2 border-t border-ink/10">
                {tradeoffs.length > 0 && (
                  <div className="mb-3 space-y-1.5">
                    <h4 className="text-xs font-extrabold uppercase text-ink/40 mb-1">Yang perlu dipertimbangkan</h4>
                    {tradeoffs.map((t) => (
                      <p key={t} className="flex items-start gap-2 text-sm text-ink/50">
                        <AlertCircle size={14} className="shrink-0 mt-0.5" /><span>{t}</span>
                      </p>
                    ))}
                  </div>
                )}
                <h4 className="text-xs font-extrabold uppercase text-ink/40 mb-1">Breakdown Skor</h4>
                <MatchBreakdown breakdown={breakdown} />
              </div>
            </div>
          </div>

          {/* Bottom: Actions */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 border-t border-ink/10 pt-4">
            <Button to={`/schools/${school.slug}`} size="sm" className="flex-1 justify-center text-sm">
              <Eye size={16} className="inline mr-1.5" /> Lihat Detail Sekolah
            </Button>
            <Button
              variant={inCompare ? "mint" : "secondary"}
              size="sm"
              onClick={onCompare}
              className="flex-1 justify-center text-sm"
            >
              {inCompare
                ? <><Check size={16} className="inline mr-1" /> Dibandingkan</>
                : <><Scale size={16} className="inline mr-1" /> Bandingkan</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
